import errorHandler from './utils/errorHandler';
import axios from 'axios';

const api = axios.create({
    baseURL: '/api',
    headers: {
        'Content-Type': 'application/json'
    }
});

const logApiCall = (config) => {
    console.log('API Request Details:', {
        method: config.method,
        url: config.url,
        baseURL: config.baseURL,
        fullURL: `${config.baseURL}${config.url}`,
        hasToken: !!localStorage.getItem('access_token')
    });
};

const handleAuthError = () => {
    localStorage.clear();
    window.location.replace(window.location.origin + '/login');  // replace not href — no back button
};

// =============================================================================
// HMRC Fraud Prevention Headers
// =============================================================================

function collectFraudPreventionHeaders() {
    // Device ID - persistent across sessions
    let deviceId = localStorage.getItem('salonlogic_device_id');
    if (!deviceId) {
        deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = Math.random() * 16 | 0;
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
        localStorage.setItem('salonlogic_device_id', deviceId);
    }
    // Timezone in UTC offset format
    const offset = new Date().getTimezoneOffset();
    const hours = Math.floor(Math.abs(offset) / 60);
    const mins = Math.abs(offset) % 60;
    const timezone = `UTC${offset <= 0 ? '+' : '-'}${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
    // Screen info
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    // Window size can never legitimately exceed screen size — clamp
    // defensively in case of OS display-scaling quirks reporting
    // innerWidth/innerHeight inconsistently with screen.width/height.
    const windowWidth = Math.min(window.innerWidth, screenWidth);
    const windowHeight = Math.min(window.innerHeight, screenHeight);

    const screenInfo = `width=${screenWidth}&height=${screenHeight}&scaling-factor=${window.devicePixelRatio || 1}&colour-depth=${window.screen.colorDepth}`;
    const windowSize = `width=${windowWidth}&height=${windowHeight}`;

    // TEMP — leave in for the next test round, remove once confirmed clean
    console.log('FPH screen/window check:', { screenWidth, screenHeight, rawInnerWidth: window.innerWidth, rawInnerHeight: window.innerHeight, windowWidth, windowHeight });

    const headers = {
        'X-FP-Device-Id': deviceId,
        'X-FP-Timezone': timezone,
        'X-FP-Browser-UA': navigator.userAgent,
        'X-FP-Do-Not-Track': navigator.doNotTrack === '1' ? 'true' : 'false',
        'X-FP-Collected-At': new Date().toISOString(),
        'X-FP-Screens': screenInfo,
        'X-FP-Window-Size': windowSize
    };
    return headers;
}

// =============================================================================
// Request interceptor
// =============================================================================

api.interceptors.request.use(
    (config) => {
        // Don't add token for login
        if (config.url === '/auth/login') {
            delete config.headers.Authorization;
            logApiCall(config);
            return config;
        }

        const accessToken = localStorage.getItem('access_token');
        if (accessToken && accessToken !== 'undefined') {
            config.headers.Authorization = `Bearer ${accessToken}`;
        } else {
            delete config.headers.Authorization;
        }

        // Add fraud prevention headers for HMRC routes
        if (config.url && /^\/(hmrc|solo\/hmrc-itsa)(\/|$)/.test(config.url)) {
            const fpHeaders = collectFraudPreventionHeaders();
            Object.assign(config.headers, fpHeaders);
            console.log('HMRC Fraud Prevention Headers added:', Object.keys(fpHeaders));
        }

        logApiCall(config);
        return config;
    },
    (error) => Promise.reject(error)
);

// =============================================================================
// Response interceptor
// =============================================================================

// SINGLE-FLIGHT REFRESH LOCK
// If multiple requests 401 at (near) the same moment — e.g. a dashboard
// firing several parallel API calls right as the access token expires —
// each would otherwise independently call /api/auth/refresh. If the
// backend rotates/invalidates the refresh token on use (or even just
// under normal race conditions), only the first of those concurrent
// calls can succeed; the rest fail and each independently forces a full
// logout via handleAuthError(), even though the session is actually
// fine. This lock ensures only ONE refresh call is ever in flight;
// every other 401'd request just waits on the same promise and retries
// once it resolves.
let refreshPromise = null;

function refreshAccessToken() {
    if (refreshPromise) {
        return refreshPromise;
    }

    refreshPromise = (async () => {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
            throw new Error('NO_REFRESH_TOKEN');
        }

        const response = await axios.post('/api/auth/refresh', {
            refresh_token: refreshToken
        });

        if (!response.data.access_token) {
            throw new Error('REFRESH_RESPONSE_MISSING_ACCESS_TOKEN');
        }

        localStorage.setItem('access_token', response.data.access_token);

        // IMPORTANT: if the backend rotates refresh tokens (issues a new
        // one and invalidates the old one on each use), the new token
        // MUST be captured here — otherwise the next refresh attempt
        // sends an already-invalidated token and the user gets silently
        // logged out mid-session, even though nothing was actually wrong.
        // Harmless no-op if the backend does NOT rotate refresh tokens.
        if (response.data.refresh_token) {
            localStorage.setItem('refresh_token', response.data.refresh_token);
        }

        return response.data.access_token;
    })();

    // Clear the lock once settled (success or failure) so the NEXT
    // separate expiry cycle can trigger a fresh refresh attempt.
    refreshPromise.finally(() => {
        refreshPromise = null;
    });

    return refreshPromise;
}

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Don't retry for login endpoint
        if (originalRequest.url === '/auth/login') {
            return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const newAccessToken = await refreshAccessToken();

                api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                return api(originalRequest);
            } catch (refreshError) {
                if (refreshError.message === 'NO_REFRESH_TOKEN') {
                    // No refresh token — user is not logged in to the main app.
                    // Do NOT redirect; let the calling code handle the rejection.
                    // This allows self-authenticating routes (e.g. employee portal
                    // PIN login) to operate without triggering a main app logout.
                    return Promise.reject(error);
                }
                handleAuthError();
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export default api;